import { cloneDeep } from 'lodash';
import { Dispatch } from 'redux';
import { IceBreakerButtonController } from '../controllers/IceBreakerButtonController';
import commonHandler from '../errorHandlers/common-handler';
import ButtonModel, {
  GenericButtonModel,
  PageButtonConfig,
} from '../model/ButtonModel';
import { IceBreakerKind } from '../model/IceBreakerModel';
import { StoreState } from '../store-interfaces';
import { ICE_BREAKER } from '../syncEngine/constants/entityTypes';
import ButtonChangeManager from '../syncEngine/services/changeManagers/ButtonChangeManager';
import { getActionHookBuilder } from './action-hook-builder';
import CrudActionContainer from './CrudActionContainer';
import IceBreakerCRUDActions from './IceBreakerCRUDActions';

type PageButtonModel = GenericButtonModel<PageButtonConfig>;

class IceBreakerButtonCRUDActions extends CrudActionContainer {
  constructor() {
    super('ICE_BREAKER_BUTTON');
  }

  create(
    botId: number,
    iceBreakerId: number,
    kind: IceBreakerKind,
    button: Partial<PageButtonModel>,
    skipBusy = false,
    withPatch = true,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      if (!skipBusy) {
        dispatch(this.busy(this.createAction));
      }

      try {
        const response = await IceBreakerButtonController.postIceBreakerButton(
          botId,
          iceBreakerId,
          button,
        );
        dispatch(this.retrievedButton(iceBreakerId, response));
        if (withPatch) {
          dispatch(IceBreakerCRUDActions.retrieveAll(botId, kind));
        }

        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  update(
    botId: number,
    iceBreakerId: number,
    kind: IceBreakerKind,
    button: PageButtonModel,
    withPatch = true,
    skipBusy = false,
  ) {
    return async (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ): Promise<PageButtonModel> => {
      const {
        iceBreakers: {
          [kind]: { entities: iceBreakers },
        },
      } = getState();
      const iceBreakerModel = iceBreakers.find(
        iceBreaker => iceBreaker.id === iceBreakerId,
      );

      if (!iceBreakerModel) {
        return Promise.reject('iceBreakerModel does not exist');
      }

      const prevButtonValue =
        iceBreakerModel.buttons.find(b => b.id === button.id) || null;

      if (!skipBusy) {
        dispatch(this.busy(this.updateAction));
      }
      const buttonWithIceBreakerId = { ...cloneDeep(button), iceBreakerId };

      dispatch(this.updateLocal(buttonWithIceBreakerId));

      try {
        const response = (await ButtonChangeManager.update(
          prevButtonValue,
          button,
          iceBreakerId,
          ICE_BREAKER,
          botId,
        )) as PageButtonModel;

        if (withPatch) {
          dispatch(this.updateLocal({ ...response, iceBreakerId }));
          dispatch(IceBreakerCRUDActions.retrieveAll(botId, kind));
        }
        return response;
      } catch (e) {
        return commonHandler(dispatch)(e);
      }
    };
  }

  delete(
    botId: number,
    iceBreakerId: number,
    kind: IceBreakerKind,
    button: PageButtonModel,
    withPatch = true,
    skipBusy = false,
  ) {
    return async (dispatch: Dispatch<StoreState>): Promise<PageButtonModel> => {
      if (!skipBusy) {
        dispatch(this.busy(this.deleteAction));
      }

      const buttonWithIceBreakerId = { ...button, iceBreakerId };
      dispatch(this.deleteLocal(buttonWithIceBreakerId));

      try {
        const response = (await ButtonChangeManager.delete(
          button,
          iceBreakerId,
          ICE_BREAKER,
          botId,
        )) as PageButtonModel;

        if (withPatch) {
          dispatch(IceBreakerCRUDActions.retrieveAll(botId, kind));
        }
        return response;
      } catch (e) {
        return commonHandler(dispatch)(e);
      }
    };
  }

  retrievedButton = (iceBreakerId: number, button: ButtonModel) => ({
    type: this.retrievedAction,
    payload: { button, iceBreakerId },
  });
}

const iceBreakerButtonCRUDActions = new IceBreakerButtonCRUDActions();

export default iceBreakerButtonCRUDActions;

export const useIceBreakerButtonCRUDActions = getActionHookBuilder(
  iceBreakerButtonCRUDActions,
  ['create', 'update', 'delete'],
);
